import clsx from "clsx";
import React, { useContext } from "react";
import {
  Container,
  ContainerProps,
  Menu,
  MenuItem,
  Typography,
  Divider,
  darken,
  Box,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MenuRounded } from "@mui/icons-material";

import { TeamRole, UserRole } from "../../constants";
import { Link } from "../link/Link";
import { GroupNavItem } from "../groups/GroupNavItem";

import { menuItems } from "./constants";
import { CurrentUserBox } from "./CurrentUserBox";
import { AppLogo } from "./AppLogo";
import {
  COACH_CLIENT_PORTAL_ROUTE,
  COACH_LIBRARY_ROUTE,
  COACH_PROGRAMS_PUBLISHED_ROUTE,
  COACH_TEAM_ROUTE,
  HOME_ROUTE,
} from "../../routes/routes";
import { useCurrentUserRole } from "../../hooks/useCurrentUser";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { isDrawerItemSelected } from "./common";
import { LibraryNavItem } from "../exercise-library/LibraryNavItem";
import { JourneyLauncher } from "./JourneyLauncher";
import { JourneyRouteManager } from "../../routes/admin/onboardingTemplate/JourneyRouteManager";
import { JourneyRunContext } from "../../contexts/JourneyRunContext";
import { DrawerAnnouncement } from "./DrawerAnnouncement";
import { useQuery } from "@tanstack/react-query";
import { DRAWER_ANNOUNCEMENT_QUERY_KEY } from "./AppDrawer";
import AnnouncementsService from "../../services/AnnouncementsService";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: theme.breakpoints.values.llg,
    display: "flex",
    justifyContent: "left",
    alignItems: "center",
    paddingTop: theme.spacing(1),
    textDecoration: "none",
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: "sticky",
  },

  link: {
    lineHeight: 0,
  },

  logo: {
    width: "100%",
    height: "auto",
    maxWidth: 60,
    maxHeight: 42,
  },

  menuIcon: {
    fontSize: 32,
    marginLeft: theme.spacing(1),
    cursor: "pointer",
  },

  menu: {
    minWidth: 260,
    maxWidth: 300,
    borderRadius: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(-6),
  },

  itemLink: {
    textDecoration: "none !important",
    "&:hover": {
      textDecoration: "none !important",
    },
  },

  menuItem: {
    paddingLeft: theme.spacing(4),
  },

  icon: {
    color: theme.palette.text.secondary,
    "&$active": {
      "& *": {
        stroke: theme.palette.primary.main,
      },
    },
  },

  itemText: {
    fontSize: 20,
    fontWeight: "bold",
    lineHeight: "24px",
    color: theme.palette.text.secondary,
    padding: theme.spacing(1.25, 0, 1.25, 1.75),

    "&$active": {
      color: theme.palette.primary.main,
    },
  },

  tutorialContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    gap: theme.spacing(1),

    [theme.breakpoints.up("sm")]: {
      height: theme.spacing(6),
      minHeight: theme.spacing(6),
    },
  },

  tutorialButton: {
    color: theme.palette.text.secondary,
    textTransform: "none",
    minWidth: 0,

    "&:hover": {
      background: darken(theme.palette.common.white, 0.05),
    },
  },

  tutorialText: {
    color: theme.palette.text.secondary,
  },

  active: {},
}));

export interface AppBarNavProps extends ContainerProps {}

export function AppBarNav(props: AppBarNavProps) {
  const { className, children, ...other } = props;
  const s = useStyles();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);

  const mode = useCurrentUserRole();

  const { data: announcement } = useQuery({
    queryKey: [DRAWER_ANNOUNCEMENT_QUERY_KEY],
    queryFn: AnnouncementsService.getCurrentAnnouncement,
    staleTime: 3_600_000, // 1 hour
    enabled: mode === UserRole.COACH,
  });

  const me = useCurrentUser();
  const teamRole = me?.teamRole;
  const items = React.useMemo(() => {
    switch (mode) {
      case UserRole.CLIENT:
        return menuItems.client;
      case UserRole.COACH:
        return menuItems.coach.filter(
          (item) => !item.role || item.role === teamRole,
        );
      default:
        throw new Error(`Unknown menu item mode ${mode}`);
    }
  }, [mode, teamRole]);

  const openMenu = React.useCallback((event) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = React.useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleItemClick = React.useCallback(() => {
    closeMenu();
  }, [closeMenu]);

  const handleUserMenuClose = React.useCallback((closeAppDrawer: boolean) => {
    if (closeAppDrawer) {
      closeMenu();
    }
  }, []);

  const { setIsRunning, setIsForced } = useContext(JourneyRunContext);
  const handleJourneyButtonClick = React.useCallback(() => {
    closeMenu();
    setIsForced(true);
    setIsRunning(true);
  }, [closeMenu]);

  return (
    <Container className={clsx(s.root, className)} {...other}>
      <Link
        className={s.link}
        href={
          mode === UserRole.CLIENT ? HOME_ROUTE : COACH_PROGRAMS_PUBLISHED_ROUTE
        }
        children={<AppLogo size={42} className={s.logo} />}
      />

      <MenuRounded className={s.menuIcon} onClick={openMenu} />

      {children}

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={closeMenu}
        PaperProps={{
          className: s.menu,
        }}
      >
        {items.map(({ text, link, Icon, type }) =>
          type === "divider" ? (
            <Divider
              orientation="horizontal"
              sx={{ mx: 2, my: 1, borderBottomWidth: 1 }}
            />
          ) : link.includes("/groups") ? (
            <GroupNavItem key={link} />
          ) : link.includes(COACH_LIBRARY_ROUTE) ? (
            <LibraryNavItem key={link} />
          ) : (
            <Link
              className={s.itemLink}
              key={link}
              href={link}
              target={link.includes("marketing") && "_blank"}
              onClick={handleItemClick}
            >
              <MenuItem className={s.menuItem}>
                <Icon
                  className={clsx({
                    [s.icon]: true,
                    [s.active]: isDrawerItemSelected(link, mode),
                  })}
                />
                <Typography
                  className={clsx({
                    [s.itemText]: true,
                    [s.active]: isDrawerItemSelected(link, mode),
                  })}
                  children={text}
                />
              </MenuItem>
            </Link>
          ),
        )}

        <Box sx={{ mb: 5 }} />

        {announcement?.isActive && (
          <DrawerAnnouncement
            content={JSON.parse(announcement?.content || "[]")}
            buttonText={announcement?.buttonText}
            link={announcement?.buttonLink}
            animation={false}
          />
        )}

        <JourneyLauncher
          wrapperClassName={s.tutorialContainer}
          buttonClassName={s.tutorialButton}
          onStart={handleJourneyButtonClick}
        />
        <Divider />

        <CurrentUserBox onClose={handleUserMenuClose} />
      </Menu>
    </Container>
  );
}
